import {
  isValidEightDigitBusinessClientIdentifier,
  normalizeEightDigitBusinessClientIdentifier,
} from './businessClientRegistrationIdentifier';

export const BUSINESS_WORKER_IDENTIFIER_FIELD = 'business_client_identifier' as const;

/**
 * Not collected on business-worker registration — country/region come from the
 * affiliated business client.
 */
export const BUSINESS_WORKER_REGISTER_OMIT_KEYS = [
  'gender',
  'address',
  'country_id',
  'state_id',
] as const;

export function isBusinessWorkerRegisterFlow(selectedOption: string): boolean {
  return selectedOption === 'business worker';
}

function omitBusinessWorkerRegisterFields(payload: Record<string, unknown>): Record<string, unknown> {
  const out = { ...payload };
  for (const key of BUSINESS_WORKER_REGISTER_OMIT_KEYS) {
    delete out[key];
  }
  return out;
}

/** Business-worker register fields shown in DynamicRecordForm. */
export function businessWorkerRegisterVisibleData(
  payload: Record<string, unknown>,
  options?: { omitBusinessClientIdentifier?: boolean },
): Record<string, unknown> {
  const out = omitBusinessWorkerRegisterFields(payload);
  if (options?.omitBusinessClientIdentifier) {
    delete out[BUSINESS_WORKER_IDENTIFIER_FIELD];
  }
  return out;
}

export function businessWorkerRegisterMandatoryKeys(
  options?: { requireBusinessClientIdentifier?: boolean },
): readonly string[] {
  const keys = [
    'email',
    'password',
    'confirm_password',
    'phone_number',
    'national_id',
    'first_name',
    'last_name',
    'birth_date',
    'position',
  ] as const;
  if (options?.requireBusinessClientIdentifier) {
    return [BUSINESS_WORKER_IDENTIFIER_FIELD, ...keys];
  }
  return keys;
}

function isEmptyRegisterValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  return false;
}

/** First missing required root field key, or null when the form can submit. */
export function missingBusinessWorkerRegisterRequiredField(
  payload: Record<string, unknown>,
  options?: { requireBusinessClientIdentifier?: boolean; skipPassword?: boolean },
): string | null {
  for (const key of businessWorkerRegisterMandatoryKeys({
    requireBusinessClientIdentifier: options?.requireBusinessClientIdentifier,
  })) {
    if (
      options?.skipPassword &&
      (key === 'password' || key === 'confirm_password')
    ) {
      continue;
    }
    if (key === BUSINESS_WORKER_IDENTIFIER_FIELD) {
      if (!isValidEightDigitBusinessClientIdentifier(payload[key])) {
        return key;
      }
      continue;
    }
    if (isEmptyRegisterValue(payload[key])) {
      return key;
    }
  }
  return null;
}

export function prepareBusinessWorkerRegisterPostBody(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const out = omitBusinessWorkerRegisterFields({ ...payload });
  delete out.confirm_password;

  const identifier = normalizeEightDigitBusinessClientIdentifier(
    out[BUSINESS_WORKER_IDENTIFIER_FIELD],
  );
  if (identifier) {
    out[BUSINESS_WORKER_IDENTIFIER_FIELD] = identifier;
  } else {
    delete out[BUSINESS_WORKER_IDENTIFIER_FIELD];
  }

  return out;
}
